import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiMapPin, FiBriefcase, FiUpload } from 'react-icons/fi';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CareerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [career, setCareer] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  
  // Application form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    resume: null,
    coverLetter: null
  });

  useEffect(() => {
    const fetchCareer = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/careers/${id}`);
        if (!response.ok) {
          navigate('/careers');
          return;
        }
        const data = await response.json();
        setCareer(data);
      } catch (error) {
        console.error('Error fetching career:', error);
        navigate('/careers');
      }
    };

    fetchCareer();
  }, [id, navigate]);

  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [type]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BASE_URL}/api/careers/${id}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          careerId: id,
          jobTitle: career.jobTitle,
          jobCode: career.jobCode
        }),
      });

      if (response.ok) {
        alert('Application submitted successfully!');
        setIsApplying(false);
      } else {
        alert('Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application. Please try again.');
    }
  };

  if (!career) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Job Header */}
          <div className="border-b pb-6 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{career.jobTitle}</h1>
            <div className="flex flex-wrap gap-4 text-gray-600">
              <div className="flex items-center gap-2">
                <FiMapPin className="text-indigo-600" />
                <span>{career.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiBriefcase className="text-indigo-600" />
                <span>{career.jobType}</span>
              </div>
              <div className="text-sm">
                Job Code: {career.jobCode}
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div className="prose max-w-none mb-8" 
               dangerouslySetInnerHTML={{ __html: career.description }}>
          </div>

          {/* Apply Button */}
          {!isApplying && (
            <button
              onClick={() => setIsApplying(true)}
              className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700
                       transition-colors font-semibold text-lg"
            >
              Apply Now
            </button>
          )}

          {/* Application Form */}
          {isApplying && (
            <form onSubmit={handleSubmit} className="space-y-6 border-t pt-6">
              <h2 className="text-2xl font-bold text-gray-900">Submit Your Application</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 
                             focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 
                             focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 
                             focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Cover Message</label>
                <textarea
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 
                           focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Resume</label>
                  <div className="mt-1 flex items-center">
                    <label className="flex items-center justify-center w-full border-2 border-dashed 
                                    border-gray-300 rounded-lg p-4 cursor-pointer hover:border-indigo-500">
                      <FiUpload className="mr-2" />
                      <span>{formData.resume ? 'Resume uploaded' : 'Upload Resume'}</span>
                      <input
                        type="file"
                        required
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'resume')}
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Cover Letter</label>
                  <div className="mt-1 flex items-center">
                    <label className="flex items-center justify-center w-full border-2 border-dashed 
                                    border-gray-300 rounded-lg p-4 cursor-pointer hover:border-indigo-500">
                      <FiUpload className="mr-2" />
                      <span>{formData.coverLetter ? 'Cover Letter uploaded' : 'Upload Cover Letter'}</span>
                      <input
                        type="file"
                        required
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'coverLetter')}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700
                           transition-colors font-semibold"
                >
                  Submit Application
                </button>
                <button
                  type="button"
                  onClick={() => setIsApplying(false)}
                  className="flex-1 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300
                           transition-colors font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CareerDetails;
